import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from "react";
import LogoSVG from "../resources/svg/LogoSVG";
import { useTranslation } from "react-i18next";
import HeaderLanguageChange from "./HeaderLanguageChange";
import SendEmailButton from "../resources/components/SendEmailButton";
import useScreenSize from "../../custom-hooks/useScreenSize";
import BurgerMenuIconSVG from "../resources/svg/BurgerMenuIconSVG";
import { useClickOutOfElement } from "../../custom-hooks/useClickOutOfElement";
import { email } from "../../Config";

interface HeaderProps {
    setBackgroundBlur: Dispatch<SetStateAction<boolean>>;
    backgroundBlur: boolean;
}

const Header: FC<HeaderProps> = (props) => {
    const [burgerMenuOpened, setBurgerMenuOpened] = useState(false);
    const { upTo900, moreOrEqual900 } = useScreenSize();
    const burgerMenuRef = useRef(null);
    const headerRef = useRef(null);
    const { t } = useTranslation();
    const openBurgerMenuHandler = () => {
        setBurgerMenuOpened(!burgerMenuOpened);
        props.setBackgroundBlur(!props.backgroundBlur);
    };
    useClickOutOfElement(burgerMenuRef, () => {
        setBurgerMenuOpened(false);
        props.setBackgroundBlur(false);
    }, [headerRef]);
    useEffect(() => {
        if (!upTo900 && burgerMenuOpened) {
            props.setBackgroundBlur(false);
            setBurgerMenuOpened(false);
        }
    }, [upTo900, burgerMenuOpened]);

    return (
        <header
            ref={headerRef}
            className="relative dmin2:h-[98px] td2:h-[98px] ti4:h-[98px] tmax4:h-[98px] dmin2:pt-8 dmin2:pb-4 dmax2:py-5"
        >
            <div className="absolute flex justify-between items-center left-0 right-0 dmin2:mx-auto di6:mx-[26px] ti6:mx-8 ti7:mx-5 tmax4:mx-[15px] dmin2:max-w-[1280px] dmin2:w-[1280px] di6:min-w-[960px] ti6:min-w-[725px]">
                <LogoSVG />
                {moreOrEqual900 ? (
                    <div className="flex items-center">
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById("markets")?.scrollIntoView({
                                    behavior: "smooth",
                                });
                            }}
                            className="H16 hover:font-[500] text-black inline-block min-w-[70px] mr-8"
                        >
                            {t("header.markets")}
                        </a>
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById("aboutUs")?.scrollIntoView({
                                    behavior: "smooth",
                                });
                            }}
                            className="H16 hover:font-[500] text-black inline-block min-w-[70px] mr-8"
                        >
                            {t("header.aboutUs")}
                        </a>
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById("gallery")?.scrollIntoView({
                                    behavior: "smooth",
                                });
                            }}
                            className="H16 hover:font-[500] text-black inline-block min-w-[56px] mr-8"
                        >
                            {t("header.gallery")}
                        </a>
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById("contacts")?.scrollIntoView({
                                    behavior: "smooth",
                                });
                            }}
                            className="H16 hover:font-[500] text-black inline-block min-w-[70px] mr-8"
                        >
                            {t("header.contacts")}
                        </a>
                        <HeaderLanguageChange />
                        <SendEmailButton />
                    </div>
                ) : (
                    <div
                        onClick={openBurgerMenuHandler}
                        className="flex justify-center items-center transition-all duration-300 ease-in-out"
                    >
                        <BurgerMenuIconSVG
                            burgerMenuOpened={burgerMenuOpened}
                            setBurgerMenuOpened={setBurgerMenuOpened}
                            className="absolute right-0 cursor-pointer z-30 transition-all duration-300 ease-in-out"
                        />
                    </div>
                )}
            </div>
            <div
                ref={burgerMenuRef}
                className={`absolute top-[90px] z-10 w-full h-[300px] bg-white transition-all duration-300 ease-in-out 
                        ${
                            burgerMenuOpened
                                ? "opacity-100 translate-y-0 visible pointer-events-auto"
                                : "opacity-0 -translate-y-5 invisible pointer-events-none"
                        }`}
            >
                <div className="flex flex-col items-center">
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("markets")?.scrollIntoView({
                                behavior: "smooth",
                            });
                            props.setBackgroundBlur(false);
                            setBurgerMenuOpened(false);
                        }}
                        className="H16 hover:font-[500] text-black mb-7"
                    >
                        {t("header.markets")}
                    </a>
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("aboutUs")?.scrollIntoView({
                                behavior: "smooth",
                            });
                            props.setBackgroundBlur(false);
                            setBurgerMenuOpened(false);
                        }}
                        className="H16 hover:font-[500] text-black mb-7"
                    >
                        {t("header.aboutUs")}
                    </a>
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("gallery")?.scrollIntoView({
                                behavior: "smooth",
                            });
                            props.setBackgroundBlur(false);
                            setBurgerMenuOpened(false);
                        }}
                        className="H16 hover:font-[500] text-black mb-7"
                    >
                        {t("header.gallery")}
                    </a>
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("contacts")?.scrollIntoView({
                                behavior: "smooth",
                            });
                            props.setBackgroundBlur(false);
                            setBurgerMenuOpened(false);
                        }}
                        className="H16 hover:font-[500] text-black mb-7"
                    >
                        {t("header.contacts")}
                    </a>
                    <HeaderLanguageChange />
                    <a
                        href={`mailto:${email}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex z-20 justify-center items-center H14 border border-black text-[#FCFAF0] bg-black hover:text-black hover:bg-white transition-all duration-300 rounded-[3px] mmin1:w-[360px] mmax1:w-[320px] h-[39px] cursor-pointer"
                    >
                        {t("common.sendEmail")}
                    </a>
                </div>
            </div>
        </header>
    );
};

export default Header;
